import { useCallback, useRef, useState } from "react";
import {
    Table as ZhTable,
    type TableProps,
    type TableRef,
    type TableRequestData,
    type TableRequestResult
} from "zhihao-ui-react";

import { AdminPagination } from "@/views/components/AdminPagination";

type RequestTableProps<RecordType extends object> = Omit<
    TableProps<RecordType>,
    "dataCallback" | "pagination"
> & {
    /** 请求表格沿用旧 Vue 分页的可选条数。 */
    pageSizeOptions?: number[];
};

export interface AdminRequestTablePageable {
    current: number;
    pageSize: number;
    total: number;
}

/** 将自定义分页状态注入 zhihao Table 的请求参数，兼容 pagination=false 不自动传分页字段的实现。 */
export const buildAdminRequestTableParams = (
    params: Record<string, unknown>,
    pageable: AdminRequestTablePageable
): Record<string, unknown> => ({
    ...params,
    page: pageable.current,
    size: pageable.pageSize
});

const toRequestResult = <RecordType extends object>(
    data: TableRequestData<RecordType>,
    fallback: AdminRequestTablePageable
): TableRequestResult<RecordType> => {
    if (Array.isArray(data)) {
        return {
            list: data,
            currentPage: fallback.current,
            pageSize: fallback.pageSize,
            total: data.length
        };
    }
    return data;
};

/**
 * 请求表格的统一分页适配。
 * zhihao Table 内置 Ant Pagination 在只有一页时会隐藏快速跳转，旧 Vue ProTable 则始终显示，
 * 因此这里关闭内置分页并复用 AdminPagination，保证单页、多页和窄容器都保留完整控件。
 */
export function AdminRequestTable<RecordType extends object>({
    columns,
    initParam,
    pageSizeOptions,
    handleParam,
    ...tableProps
}: RequestTableProps<RecordType>) {
    const tableRef = useRef<TableRef<RecordType>>(null);
    const initialPage = Number(initParam?.page) || 1;
    const initialPageSize = Number(initParam?.size) || Number(pageSizeOptions?.[0]) || 10;
    const [pageable, setPageable] = useState<AdminRequestTablePageable>({
        current: initialPage,
        pageSize: initialPageSize,
        total: 0
    });
    const pageableRef = useRef<AdminRequestTablePageable>({
        current: initialPage,
        pageSize: initialPageSize,
        total: 0
    });

    const handleData = useCallback(
        (data: TableRequestData<RecordType>) => {
            const result = toRequestResult(data, pageableRef.current);
            const nextPageable = {
                current: Number(result.currentPage) || initialPage,
                pageSize: Number(result.pageSize) || initialPageSize,
                total: Number(result.total) || 0
            };
            pageableRef.current = nextPageable;
            setPageable(nextPageable);
            // Table 在 pagination=false 时要求 dataCallback 返回行数组，否则会把分页结果对象
            // 当作表格数据继续处理，最终在列/行标准化阶段触发 flatMap 类型错误。
            return result.list;
        },
        [initialPage, initialPageSize]
    );

    const buildRequestParams = useCallback(
        (params: Record<string, unknown>) => {
            const nextParams = buildAdminRequestTableParams(params, pageableRef.current);
            // 保留调用方已有的参数转换逻辑，同时确保分页字段使用用户刚选择的页码和条数。
            return handleParam ? handleParam(nextParams) : nextParams;
        },
        [handleParam]
    );

    const handlePageChange = useCallback(
        (page: number, size?: number) => {
            const previousPageable = pageableRef.current;
            const nextPageable = {
                ...previousPageable,
                current: Number(page) || previousPageable.current,
                pageSize: Number(size) || previousPageable.pageSize
            };
            // 先更新 ref，再触发 Table 请求；Table 在 pagination=false 时会通过 handleParam 读取该 ref。
            pageableRef.current = nextPageable;
            if (nextPageable.pageSize !== previousPageable.pageSize) {
                tableRef.current?.handleSizeChange(nextPageable.pageSize);
                return;
            }
            if (nextPageable.current !== previousPageable.current) {
                tableRef.current?.handleCurrentChange(nextPageable.current);
            }
        },
        []
    );

    return (
        <div className="admin-request-table">
            <ZhTable<RecordType>
                {...tableProps}
                columns={columns}
                ref={tableRef}
                initParam={initParam}
                handleParam={buildRequestParams}
                pagination={false}
                dataCallback={handleData}
            />
            {pageable.total > 0 ? (
                <div className="admin-table-composed-pagination admin-request-table__pagination">
                    <AdminPagination
                        current={pageable.current}
                        pageSize={pageable.pageSize}
                        pageSizeOptions={pageSizeOptions}
                        total={pageable.total}
                        // 统计弹窗沿用旧版 Element Plus 的“前往”文案，避免单页时控件名称缺失。
                        locale={{ jump_to: "前往" }}
                        onChange={handlePageChange}
                        onShowSizeChange={handlePageChange}
                    />
                </div>
            ) : null}
        </div>
    );
}

export default AdminRequestTable;
